import { useState, type ReactElement, type SyntheticEvent } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi } from '@/api/errors';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';

import type {
  BaseVersionsViewModel,
  CreateTenantInput,
  ServiceOverrideInput,
  TenantState,
} from '../api';
import { OVERRIDABLE_SERVICES, type OverridableServiceName } from '../endpoints';
import {
  validateOverrideDraft,
  type OverrideDraft,
  type OverrideDraftErrors,
} from '../overrides';
import { validateTenantName } from '../tenantName';

interface FieldErrors {
  name?: string;
}

interface DraftState {
  name: string;
  label: string;
  state: TenantState;
}

const EMPTY_DRAFT: DraftState = { name: '', label: '', state: 'active' };

const EMPTY_OVERRIDE_DRAFT: OverrideDraft = {
  backend: { checked: false, tag: '' },
  frontend: { checked: false, tag: '' },
  indexing: { checked: false, tag: '' },
};

function overrideDraftToInputs(draft: OverrideDraft): ServiceOverrideInput[] {
  return OVERRIDABLE_SERVICES.filter((s) => draft[s].checked).map((s) => ({
    serviceName: s,
    imageTag: draft[s].tag.trim(),
  }));
}

type TouchedTags = Partial<Record<OverridableServiceName, boolean>>;

export function CreateTenantDialog({
  open,
  onOpenChange,
  onSubmit,
  pending,
  submitError,
  dmAvailable,
  baseVersions,
}: {
  readonly open: boolean;
  readonly onOpenChange: (next: boolean) => void;
  readonly onSubmit: (input: CreateTenantInput) => void;
  readonly pending: boolean;
  readonly submitError: unknown;
  /** When false, the override section is hidden entirely (production). */
  readonly dmAvailable: boolean;
  /**
   * Base image tags the cluster runs, used to pre-fill each service's tag input
   * (PTV2-1021 Phase 4). `null` (DM unavailable / not yet loaded) → no pre-fill,
   * inputs stay empty. A `null` per-service value (service not deployed) → that
   * input stays empty with a "not deployed" hint. The pre-fill seeds only the
   * untouched initial state; values the operator has typed are never clobbered.
   */
  readonly baseVersions: BaseVersionsViewModel | null;
}): ReactElement {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [overridesOpen, setOverridesOpen] = useState(false);
  const [overrideDraft, setOverrideDraft] = useState<OverrideDraft>(EMPTY_OVERRIDE_DRAFT);
  const [overrideErrors, setOverrideErrors] = useState<OverrideDraftErrors>({});
  // Per-service flag: the operator has manually edited this tag, so the
  // base-version pre-fill must NOT overwrite it.
  const [touchedTags, setTouchedTags] = useState<TouchedTags>({});

  function resetAll(): void {
    setDraft(EMPTY_DRAFT);
    setErrors({});
    setOverridesOpen(false);
    setOverrideDraft(EMPTY_OVERRIDE_DRAFT);
    setOverrideErrors({});
    setTouchedTags({});
  }

  function handleClose(next: boolean): void {
    if (!next) resetAll();
    onOpenChange(next);
  }

  function setOverrideEntry(
    service: OverridableServiceName,
    patch: Partial<{ checked: boolean; tag: string }>,
  ): void {
    setOverrideDraft((prev) => ({ ...prev, [service]: { ...prev[service], ...patch } }));
  }

  /**
   * Checkbox handler: on first check, seed the tag from the cluster's base
   * version IF the operator has not already typed one (untouched) and the base
   * version is present. Unchecking leaves the tag as-is (so re-checking keeps
   * the operator's value); the tag is only sent when the box is checked.
   */
  function handleToggleService(service: OverridableServiceName, checked: boolean): void {
    setOverrideDraft((prev) => {
      const entry = prev[service];
      const baseTag = baseVersions?.[service] ?? null;
      const shouldSeed =
        checked && !touchedTags[service] && entry.tag.length === 0 && baseTag !== null;
      return {
        ...prev,
        [service]: { checked, tag: shouldSeed ? baseTag : entry.tag },
      };
    });
  }

  function handleTagChange(service: OverridableServiceName, value: string): void {
    setTouchedTags((prev) => ({ ...prev, [service]: true }));
    setOverrideEntry(service, { tag: value });
  }

  function handleSubmit(event: SyntheticEvent): void {
    event.preventDefault();
    const name = draft.name.trim();
    const problem = validateTenantName(name);
    if (problem !== null) {
      setErrors({
        name:
          problem === 'required'
            ? t('routes.platform.tenants.create.errors.nameRequired')
            : problem === 'reserved'
              ? t('routes.platform.tenants.create.errors.nameReserved', { name })
              : t('routes.platform.tenants.create.errors.nameFormat'),
      });
      return;
    }
    setErrors({});

    // Only validate overrides when DM is available (otherwise the section can't
    // even be opened, but guard defensively).
    const overrideInputs = dmAvailable ? overrideDraftToInputs(overrideDraft) : [];
    if (dmAvailable) {
      const oErrors = validateOverrideDraft(overrideDraft);
      if (Object.keys(oErrors).length > 0) {
        setOverrideErrors(oErrors);
        setOverridesOpen(true);
        return;
      }
      setOverrideErrors({});
    }

    const label = draft.label.trim();
    onSubmit({
      name,
      ...(label.length > 0 ? { label } : {}),
      state: draft.state,
      ...(overrideInputs.length > 0 ? { serviceOverrides: overrideInputs } : {}),
    });
  }

  const submitErrorCode = getErrorCodeForUi(submitError);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent disableOutsideClose>
        <DialogHeader>
          <DialogTitle>{t('routes.platform.tenants.create.title')}</DialogTitle>
          <DialogDescription>
            {t('routes.platform.tenants.create.description')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <FormField
            id="create-tenant-name"
            label={t('routes.platform.tenants.create.fields.name')}
            required
            error={errors.name}
          >
            {(fp) => (
              <Input
                {...fp}
                value={draft.name}
                onChange={(e) => { setDraft((p) => ({ ...p, name: e.target.value })); }}
                autoComplete="off"
                spellCheck={false}
                className={`${fp.className} font-mono`}
              />
            )}
          </FormField>
          <p className="-mt-2 text-xs text-muted-foreground">
            {t('routes.platform.tenants.create.hints.name')}
          </p>
          <FormField
            id="create-tenant-label"
            label={t('routes.platform.tenants.create.fields.label')}
          >
            {(fp) => (
              <Input
                {...fp}
                value={draft.label}
                onChange={(e) => { setDraft((p) => ({ ...p, label: e.target.value })); }}
                autoComplete="off"
              />
            )}
          </FormField>
          <FormField
            id="create-tenant-state"
            label={t('routes.platform.tenants.create.fields.state')}
          >
            {(fp) => (
              <Select
                {...fp}
                value={draft.state}
                onChange={(e) =>
                  { setDraft((p) => ({ ...p, state: e.target.value as TenantState })); }
                }
              >
                <option value="active">{t('routes.platform.tenants.state.active')}</option>
                <option value="inactive">{t('routes.platform.tenants.state.inactive')}</option>
              </Select>
            )}
          </FormField>

          {dmAvailable && (
            <div className="rounded-md border border-border">
              <button
                type="button"
                aria-expanded={overridesOpen}
                onClick={() => { setOverridesOpen((v) => !v); }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium"
              >
                {overridesOpen ? (
                  <ChevronDown className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <ChevronRight className="h-4 w-4" aria-hidden="true" />
                )}
                {t('routes.platform.tenants.create.overrides.toggle')}
              </button>
              {overridesOpen && (
                <div className="space-y-3 border-t border-border px-3 py-3">
                  <p className="text-xs text-muted-foreground">
                    {t('routes.platform.tenants.create.overrides.hint')}
                  </p>
                  {OVERRIDABLE_SERVICES.map((service) => {
                    const entry = overrideDraft[service];
                    const hasError = overrideErrors[service] !== undefined;
                    // A service with a null base tag is not deployed on the
                    // cluster — surface a subtle hint so the operator knows the
                    // empty input is expected (vs the pre-fill having failed).
                    const notDeployed =
                      baseVersions !== null && baseVersions[service] === null;
                    return (
                      <div key={service} className="space-y-1">
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={entry.checked}
                            onChange={(e) => { handleToggleService(service, e.target.checked); }}
                            className="h-4 w-4 rounded border-border"
                          />
                          <span className="font-mono">{service}</span>
                          {notDeployed && (
                            <span className="text-xs font-normal text-muted-foreground">
                              {t('routes.platform.tenants.create.overrides.notDeployed')}
                            </span>
                          )}
                        </label>
                        {entry.checked && (
                          <>
                            <Input
                              aria-label={t('routes.platform.tenants.create.overrides.serviceLabel', {
                                service,
                              })}
                              value={entry.tag}
                              onChange={(e) => { handleTagChange(service, e.target.value); }}
                              placeholder={t('routes.platform.tenants.create.overrides.tagPlaceholder')}
                              autoComplete="off"
                              spellCheck={false}
                              className="font-mono"
                              aria-invalid={hasError}
                            />
                            {hasError && (
                              <p role="alert" className="text-xs text-red-600">
                                {t('routes.platform.tenants.create.overrides.tagRequired')}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {submitError !== undefined && submitError !== null && (
            <p role="alert" className="text-sm text-red-600">
              {t('routes.platform.tenants.create.errors.submit', { code: submitErrorCode })}
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => { handleClose(false); }}
              disabled={pending}
            >
              {t('routes.platform.tenants.create.cancel')}
            </Button>
            <Button type="submit" loading={pending}>
              {t('routes.platform.tenants.create.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
